<script lang="ts">
  import { X, Plus } from 'lucide-svelte'
  import { fileStore, openTabs, activeTab } from '$lib/stores/fileStore'
  import type { FileTab } from '$lib/stores/fileStore'
  
  /**
   * TabBar: Display and manage open document tabs
   * Single Responsibility: Tab UI and interactions
   */
  
  interface Props {
    class?: string
    onNewDocument?: () => void
  }
  
  let { class: className = '', onNewDocument }: Props = $props()
  
  let tabs = $derived($openTabs)
  let currentTab = $derived($activeTab)
  let tabContainer = $state<HTMLDivElement | null>(null)
  let isOverflowing = $state(false)
  
  // Calculate dynamic tab width based on container and tab count
  let containerWidth = $state(0)
  
  // Update container width when it changes
  $effect(() => {
    if (tabContainer) {
      containerWidth = tabContainer.clientWidth
      
      // Use ResizeObserver to track container size changes
      const observer = new ResizeObserver(() => {
        containerWidth = tabContainer.clientWidth
      })
      observer.observe(tabContainer)
      
      return () => observer.disconnect()
    }
  })
  
  let tabMaxWidth = $derived.by(() => {
    if (tabs.length === 0) return 200
    
    // Responsive widths
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
    const minWidth = isMobile ? 60 : 80
    const preferredWidth = isMobile ? 150 : 200  // Default/preferred tab width
    
    // When not overflowing, use preferred width
    if (!isOverflowing) {
      return preferredWidth
    }
    
    // When overflowing, calculate based on available space
    // The container width is already limited by max-width when overflowing
    const availableWidth = Math.max(100, containerWidth)
    
    // Calculate shrink width
    const shrinkWidth = Math.floor(availableWidth / tabs.length)
    
    // Use minimum width if shrinking too much
    if (shrinkWidth < minWidth) {
      return minWidth
    }
    
    // Use the calculated shrink width, but not more than preferred
    return Math.min(shrinkWidth, preferredWidth)
  })
  
  // Check overflow
  $effect(() => {
    if (tabContainer) {
      checkOverflow()
    }
  })
  
  // Watch for tab changes
  $effect(() => {
    tabs // trigger on tabs change
    setTimeout(() => {
      if (tabContainer) {
        checkOverflow()
      }
    }, 100)
  })
  
  function checkOverflow() {
    if (!tabContainer) return
    
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
    const preferredWidth = isMobile ? 150 : 200
    
    // Calculate total width needed with preferred tab width + new button
    const totalPreferredWidth = (tabs.length * preferredWidth) + 36 // 36px for new button
    
    // Check if tabs with preferred width would exceed container width
    // Use the parent element's width since container might be shrunk
    const availableWidth = tabContainer.parentElement?.clientWidth || containerWidth
    
    isOverflowing = totalPreferredWidth > availableWidth
  }
  
  function selectTab(id: string) {
    fileStore.setActiveTab(id)
    // Auto-scroll to selected tab after a short delay
    setTimeout(() => scrollToTab(id), 100)
  }
  
  function scrollToTab(id: string) {
    if (!tabContainer) return
    
    const tabElement = tabContainer.querySelector(`[data-tab-id="${id}"]`)
    if (tabElement) {
      const containerRect = tabContainer.getBoundingClientRect()
      const tabRect = tabElement.getBoundingClientRect()
      
      // Check if tab is out of view
      if (tabRect.left < containerRect.left || tabRect.right > containerRect.right) {
        // Scroll to center the tab
        const scrollLeft = tabElement.offsetLeft - (containerRect.width / 2) + (tabRect.width / 2)
        tabContainer.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        })
      }
    }
  }
  
  async function closeTab(id: string, event: MouseEvent) {
    event.stopPropagation()
    await fileStore.closeTab(id)
  }
  
  
  function handleMiddleClick(id: string, event: MouseEvent) {
    // Middle click to close tab
    if (event.button === 1) {
      event.preventDefault()
      fileStore.closeTab(id)
    }
  }
  
  function formatTitle(tab: FileTab): string {
    // Return full title - let CSS handle ellipsis based on actual tab width
    return tab.title
  }
  
  // Keyboard navigation
  function handleKeyDown(event: KeyboardEvent) {
    // Cmd/Ctrl + Tab: Next tab
    if ((event.metaKey || event.ctrlKey) && event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault()
      fileStore.switchTab('next')
    }
    
    // Cmd/Ctrl + Shift + Tab: Previous tab
    if ((event.metaKey || event.ctrlKey) && event.key === 'Tab' && event.shiftKey) {
      event.preventDefault()
      fileStore.switchTab('prev')
    }
    
    // Cmd/Ctrl + W: Close current tab
    if ((event.metaKey || event.ctrlKey) && event.key === 'w') {
      event.preventDefault()
      if (currentTab) {
        fileStore.closeTab(currentTab.id)
      }
    }
  }
</script>

<svelte:window onkeydown={handleKeyDown} />

<div class="tab-bar {className}">
  <div 
    class="tabs-container"
    class:overflowing={isOverflowing}
    bind:this={tabContainer}
  >
    {#each tabs as tab (tab.id)}
      <div
        class="tab"
        class:active={tab.isActive}
        class:dirty={tab.isDirty}
        data-tab-id={tab.id}
        style="width: {tabMaxWidth}px; flex: 0 0 {tabMaxWidth}px;"
        onclick={() => selectTab(tab.id)}
        onmousedown={(e) => handleMiddleClick(tab.id, e)}
        title={tab.title}
        role="button"
        tabindex="0"
        onkeydown={(e) => e.key === 'Enter' && selectTab(tab.id)}
      >
        <span class="tab-title">
          {#if tab.isDirty}
            <span class="dirty-indicator">•</span>
          {/if}
          <span class="tab-text">{formatTitle(tab)}</span>
        </span>
        <button
          class="close-btn"
          onclick={(e) => closeTab(tab.id, e)}
          title="닫기"
        >
          <X size={12} />
        </button>
      </div>
    {/each}
    
    <!-- New Document Button (Inside container when not overflowing) -->
    {#if !isOverflowing}
      <button
        class="new-doc-btn in-container"
        onclick={() => {
          onNewDocument?.()
          // Auto-scroll to end after creating new tab
          setTimeout(() => {
            if (tabContainer) {
              tabContainer.scrollTo({
                left: tabContainer.scrollWidth,
                behavior: 'smooth'
              })
            }
          }, 100)
        }}
        title="새 문서 (⌘N)"
      >
        <Plus size={14} />
      </button>
    {/if}
  </div>
  
  <!-- New Document Button (Outside container when overflowing) -->
  {#if isOverflowing}
    <button
      class="new-doc-btn"
      onclick={() => {
        onNewDocument?.()
        // Auto-scroll to end after creating new tab
        setTimeout(() => {
          if (tabContainer) {
            tabContainer.scrollTo({
              left: tabContainer.scrollWidth,
              behavior: 'smooth'
            })
          }
        }, 100)
      }}
      title="새 문서 (⌘N)"
    >
      <Plus size={14} />
    </button>
  {/if}
  
</div>

<style>
  .tab-bar {
    height: 36px;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    position: relative;
    overflow: hidden;
    -webkit-app-region: drag; /* Enable window dragging on tab bar */
    min-width: 0; /* Prevent overflow */
    max-width: 100%; /* Prevent expanding beyond container */
  }
  
  .tabs-container {
    flex: 0 1 auto; /* Shrink to content size when not overflowing */
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    scroll-behavior: smooth;
    height: 100%;
    min-width: 0; /* Allow container to shrink properly */
  }
  
  /* When tabs overflow, take full width minus button space */
  .tabs-container.overflowing {
    flex: 1 1 auto; /* Take full available width when overflowing */
    max-width: calc(100% - 36px); /* Reserve space for new button outside */
  }
  
  .tabs-container::-webkit-scrollbar {
    display: none;
  }
  
  .tab {
    /* width and flex are set dynamically via style attribute */
    height: 100%;
    padding: 0 8px 0 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    background: transparent;
    border: none;
    border-right: 1px solid var(--border-primary);
    color: var(--text-secondary);
    cursor: pointer;
    font-size: var(--ui-font-size-sm);
    transition: background 0.2s ease, color 0.2s ease;
    position: relative;
    box-sizing: border-box;
    -webkit-app-region: no-drag; /* Disable dragging on tabs */
  }
  
  .tab:hover {
    background: var(--bg-tertiary);
  }
  
  .tab.active {
    background: var(--bg-primary);
    color: var(--text-primary);
  }
  
  .tab.active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--success); /* Changed from blue to green to avoid conflict with resize handle */
  }
  
  .tab-title {
    flex: 1;
    min-width: 0; /* Important for ellipsis to work with flexbox */
    display: flex;
    align-items: center;
    gap: 4px;
    overflow: hidden;
  }
  
  .tab-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
  
  .dirty-indicator {
    color: var(--warning);
    font-size: 16px;
    line-height: 1;
    flex-shrink: 0; /* Prevent indicator from shrinking */
  }
  
  .close-btn {
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 3px;
    color: var(--text-tertiary);
    cursor: pointer;
    opacity: 0.6;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  
  .tab:hover .close-btn {
    opacity: 1;
  }
  
  .close-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
  
  /* New Document Button */
  .new-doc-btn {
    min-width: 36px;
    height: 100%;
    padding: 0 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-right: 1px solid var(--border-primary);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    -webkit-app-region: no-drag; /* Disable dragging on button */
  }
  
  /* When button is inside the container */
  .new-doc-btn.in-container {
    flex: 0 0 36px; /* Fixed width when inside container */
  }
  
  .new-doc-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    .tab-title {
      font-size: 11px;
    }
    
    .close-btn {
      width: 16px;
      height: 16px;
    }
  }
</style>